import type {
  RailPassengerPayload,
  TransfersSearchPayload,
} from './queries';

export type SearchPassengerType = 'passenger' | 'adult' | 'child' | 'youth' | 'senior';
export type SearchPassengerAgeRequirement = 'hidden' | 'optional' | 'required';
export type SearchPassengerError = 'required' | 'invalid' | 'unsupported';

export interface SearchPassengerDraft {
  type: SearchPassengerType;
  /** Texto crudo: una edad incompleta nunca se corrige ni inventa en silencio. */
  age: string;
}

export interface SearchPassengerTypeRule {
  type: SearchPassengerType;
  age: SearchPassengerAgeRequirement;
  minCount?: number;
  minAge?: number;
  maxAge?: number;
}

export interface SearchPassengerValidation {
  valid: boolean;
  errors: (SearchPassengerError | undefined)[];
}

export const MAX_SEARCH_PASSENGERS = 9;
export const DEFAULT_SEARCH_PASSENGER: SearchPassengerDraft = {
  type: 'adult',
  age: '',
};

/** Verticales cuyo contrato de búsqueda solo acepta una cantidad total. */
export const COUNT_ONLY_PASSENGER_RULES: readonly SearchPassengerTypeRule[] = [
  { type: 'passenger', age: 'hidden', minCount: 1 },
];

/** Tours puede explorar sin composición; si se informa, solo viaja el total. */
export const OPTIONAL_COUNT_ONLY_PASSENGER_RULES: readonly SearchPassengerTypeRule[] = [
  { type: 'passenger', age: 'hidden', minCount: 0 },
];

export const TRANSFER_PASSENGER_RULES: readonly SearchPassengerTypeRule[] = [
  { type: 'adult', age: 'hidden', minCount: 1 },
  { type: 'child', age: 'optional', minAge: 0, maxAge: 120 },
];

export const FERRY_PASSENGER_RULES: readonly SearchPassengerTypeRule[] = [
  { type: 'adult', age: 'optional', minCount: 1, minAge: 16, maxAge: 120 },
  { type: 'child', age: 'required', minAge: 0, maxAge: 17 },
];

export const SEARCH_REFERENCE_ADULT_AGE = 30;

/** Adapta un contador contractual al selector visual común, sin inventar edades. */
export function countOnlyPassengers(
  count: number,
  maxPassengers = MAX_SEARCH_PASSENGERS,
): SearchPassengerDraft[] {
  const normalized = Math.min(
    maxPassengers,
    Math.max(1, Math.trunc(Number.isFinite(count) ? count : 1)),
  );
  return Array.from({ length: normalized }, () => ({
    type: 'passenger',
    age: '',
  }));
}

function integerParam(
  value: string | undefined,
  fallback: number,
  min = 0,
  max = MAX_SEARCH_PASSENGERS,
): number {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.min(max, Math.max(min, Math.trunc(parsed)));
}

function legacyAges(value: string | undefined): number[] {
  return (value ?? '')
    .split(',')
    .map((age) => age.trim())
    .filter(Boolean)
    .map(Number)
    .filter((age) => Number.isInteger(age) && age >= 0 && age <= 120);
}

/** Formato común y compacto de URL: `adult,adult:67,child:8`. */
export function searchPassengersToParam(
  passengers: readonly SearchPassengerDraft[],
): string {
  return passengers
    .map(({ type, age }) => (age.trim() ? `${type}:${age.trim()}` : type))
    .join(',');
}

/**
 * Interpreta tanto el formato común como el legacy numérico (`pax=3`). Es
 * estricto a propósito: un link corrupto no puede cambiar la composición.
 */
export function searchPassengersFromParam(
  raw: string | undefined,
): SearchPassengerDraft[] | null {
  if (!raw) return null;
  if (/^[1-9]$/.test(raw)) {
    return Array.from({ length: Number(raw) }, () => ({
      ...DEFAULT_SEARCH_PASSENGER,
    }));
  }

  const entries = raw.split(',');
  if (!entries.length || entries.length > MAX_SEARCH_PASSENGERS) return null;

  const passengers: SearchPassengerDraft[] = [];
  for (const entry of entries) {
    const [type, age, extra] = entry.split(':');
    if (
      extra !== undefined ||
      !['passenger', 'adult', 'child', 'youth', 'senior'].includes(type) ||
      (age !== undefined && !/^\d{1,3}$/.test(age))
    ) {
      return null;
    }
    passengers.push({ type: type as SearchPassengerType, age: age ?? '' });
  }
  return passengers;
}

export function searchPassengerCount(
  passengers: readonly SearchPassengerDraft[],
  type: SearchPassengerType,
): number {
  return passengers.filter((passenger) => passenger.type === type).length;
}

/** Conserva las edades existentes al mover un stepper y ordena por reglas. */
export function resizeSearchPassengerType(
  passengers: readonly SearchPassengerDraft[],
  type: SearchPassengerType,
  requestedCount: number,
  rules: readonly SearchPassengerTypeRule[],
  maxPassengers = MAX_SEARCH_PASSENGERS,
): SearchPassengerDraft[] {
  const rule = rules.find((candidate) => candidate.type === type);
  if (!rule) return [...passengers];
  const others = passengers.filter((passenger) => passenger.type !== type);
  const current = passengers.filter((passenger) => passenger.type === type);
  const count = Math.min(
    maxPassengers - others.length,
    Math.max(rule.minCount ?? 0, Math.trunc(requestedCount)),
  );
  const resized = [
    ...current.slice(0, count),
    ...Array.from({ length: Math.max(0, count - current.length) }, () => ({
      type,
      age: '',
    })),
  ];
  const byType = new Map<SearchPassengerType, SearchPassengerDraft[]>();
  for (const passenger of [...others, ...resized]) {
    byType.set(passenger.type, [...(byType.get(passenger.type) ?? []), passenger]);
  }
  return rules.flatMap((candidate) => byType.get(candidate.type) ?? []);
}

export function updateSearchPassengerAge(
  passengers: readonly SearchPassengerDraft[],
  index: number,
  age: string,
): SearchPassengerDraft[] {
  return passengers.map((passenger, passengerIndex) =>
    passengerIndex === index ? { ...passenger, age } : passenger);
}

export function validateSearchPassengers(
  passengers: readonly SearchPassengerDraft[],
  rules: readonly SearchPassengerTypeRule[],
  maxPassengers = MAX_SEARCH_PASSENGERS,
): SearchPassengerValidation {
  const errors = passengers.map<SearchPassengerError | undefined>((passenger) => {
    const rule = rules.find((candidate) => candidate.type === passenger.type);
    if (!rule) return 'unsupported';
    if (rule.age === 'hidden') return undefined;
    const ageText = passenger.age.trim();
    if (!ageText) return rule.age === 'required' ? 'required' : undefined;
    if (!/^\d{1,3}$/.test(ageText)) return 'invalid';
    const age = Number(ageText);
    if (age < (rule.minAge ?? 0) || age > (rule.maxAge ?? 120)) {
      return 'invalid';
    }
    return undefined;
  });
  const countsValid = rules.every(
    (rule) => searchPassengerCount(passengers, rule.type) >= (rule.minCount ?? 0),
  );
  return {
    valid:
      (passengers.length > 0 ||
        rules.every((rule) => (rule.minCount ?? 0) === 0)) &&
      passengers.length <= maxPassengers &&
      countsValid &&
      errors.every((error) => error === undefined),
    errors,
  };
}

export function transferPassengersFromParams(
  params: Record<string, string>,
): SearchPassengerDraft[] | null {
  if (params.pax) return searchPassengersFromParam(params.pax);
  const adults = integerParam(params.adultos, 2, 1);
  const children = legacyAges(params.menores).map((age) => ({
    type: 'child' as const,
    age: String(age),
  }));
  return [
    ...Array.from({ length: adults }, () => ({ ...DEFAULT_SEARCH_PASSENGER })),
    ...children,
  ];
}

export function transferOccupantsFromPassengers(
  passengers: readonly SearchPassengerDraft[],
): TransfersSearchPayload['occupants'] | null {
  if (!validateSearchPassengers(passengers, TRANSFER_PASSENGER_RULES).valid) {
    return null;
  }
  return passengers.map((passenger) =>
    passenger.type === 'adult'
      ? { type: 'ADL' as const, age: SEARCH_REFERENCE_ADULT_AGE }
      : {
        type: 'CHL' as const,
        ...(passenger.age.trim() ? { age: Number(passenger.age) } : {}),
      });
}

export function ferryPassengersFromParams(
  params: Record<string, string>,
): SearchPassengerDraft[] | null {
  if (params.pax && !/^[1-9]$/.test(params.pax)) {
    return searchPassengersFromParam(params.pax);
  }

  const detailedAges = legacyAges(params.edades);
  if (detailedAges.length) {
    const adultCount = integerParam(params.adultos, detailedAges.length, 1);
    return detailedAges.map((age, index) => ({
      type: index < adultCount ? 'adult' : 'child',
      age: String(age),
    }));
  }

  const adults = integerParam(params.adultos ?? params.pax, 1, 1);
  const adultAges = legacyAges(params.edadesAdultos);
  const childAges = legacyAges(params.menores);
  return [
    ...Array.from({ length: adults }, (_, index) => ({
      type: 'adult' as const,
      age: adultAges[index] === undefined ? '' : String(adultAges[index]),
    })),
    ...childAges.map((age) => ({ type: 'child' as const, age: String(age) })),
  ];
}

export function ferryPayloadPassengersFromDrafts(
  passengers: readonly SearchPassengerDraft[],
): { passengers: number; passengerAges: number[] } | null {
  if (
    passengers[0]?.type !== 'adult' ||
    !validateSearchPassengers(passengers, FERRY_PASSENGER_RULES).valid
  ) {
    return null;
  }
  return {
    passengers: passengers.length,
    passengerAges: passengers.map((passenger) =>
      passenger.age.trim() ? Number(passenger.age) : SEARCH_REFERENCE_ADULT_AGE),
  };
}

/** Conserva `pax=N` hasta que el asesor edita la composición hidratada. */
export function ferryDraftPassengerParam(
  committedParams: Record<string, string>,
  passengers: readonly SearchPassengerDraft[],
  edited: boolean,
): string {
  if (
    !edited &&
    /^[1-9]$/.test(committedParams.pax ?? '') &&
    !committedParams.edades &&
    !committedParams.adultos &&
    !committedParams.menores &&
    !committedParams.edadesAdultos
  ) {
    return committedParams.pax;
  }
  return searchPassengersToParam(passengers);
}

/** URL → pasajeros de payload, preservando `pax=N` exactamente como legacy. */
export function ferryPayloadPassengersFromParams(
  params: Record<string, string>,
): { passengers: number; passengerAges?: number[] } | null {
  if (
    /^[1-9]$/.test(params.pax ?? '') &&
    !params.edades &&
    !params.adultos &&
    !params.menores &&
    !params.edadesAdultos
  ) {
    return { passengers: Number(params.pax) };
  }
  const drafts = ferryPassengersFromParams(params);
  return drafts ? ferryPayloadPassengersFromDrafts(drafts) : null;
}

export function railPassengerRules(
  supportedTypes: readonly RailPassengerPayload['type'][],
  typed: boolean,
): SearchPassengerTypeRule[] {
  if (!typed) return [{ type: 'adult', age: 'hidden', minCount: 1 }];
  return supportedTypes.map((type) => ({
    type,
    age: type === 'adult' ? 'optional' : 'required',
    minAge: 0,
    maxAge: 120,
  }));
}
